import React, { useMemo, useState } from "react";
import { LineChart } from "@mui/x-charts/LineChart";

import { formatMonthLabel } from "../../../../../utils";
import { BREAKPOINTS, GRAPH_COLORS } from "../../../../../utils/constants";
import { useResponsive } from "../../../../../hooks/useResponsive";
import {
  SegmentedControl,
  SegmentedControlOption,
} from "../../../../../components/SegmentedControl";
import {
  buildBalanceChartData,
  buildLegendItems,
  buildRateChartData,
  buildTrendCopy,
  buildTrendSeries,
  buildYAxisFormatter,
  buildYearTickInterval,
} from "./helpers";
import { LiabilityTrendsProps, TrendTabKey } from "./types";
import {
  ChartArea,
  DesktopControl,
  EmptyMessage,
  HeaderRow,
  HelperNote,
  LegendItem,
  LegendRow,
  LegendSwatch,
  MobileControl,
  Subtitle,
  Title,
  TitleBlock,
  TrendsCard,
} from "./styles";

const BALANCE_COLOR = "var(--color-primary)";
const RATE_COLOR = GRAPH_COLORS[2];

const MOBILE_CHART_HEIGHT = 280;
const DESKTOP_CHART_HEIGHT = 340;

const TAB_OPTIONS: SegmentedControlOption<TrendTabKey>[] = [
  { value: "balance", label: "Balance" },
  { value: "rate", label: "Interest rate" },
];

const LiabilityTrendsComponent: React.FC<LiabilityTrendsProps> = ({
  id,
  liability,
  points,
  isLoading,
}) => {
  const isCompact = useResponsive(BREAKPOINTS.laptopMin);
  const [activeTab, setActiveTab] = useState<TrendTabKey>("balance");

  const balanceData = useMemo(() => buildBalanceChartData(points), [points]);
  const rateData = useMemo(() => buildRateChartData(liability), [liability]);

  const isBalance = activeTab === "balance";
  const chartData = isBalance ? balanceData : rateData;
  const hasData = chartData.values.length > 0;

  const seriesColor = isBalance ? BALANCE_COLOR : RATE_COLOR;
  const gradientId = `liability-trend-${activeTab}-${id}`;

  const yearTickInterval = useMemo(
    () => buildYearTickInterval(chartData.labels),
    [chartData.labels],
  );

  const { title, subtitle } = buildTrendCopy(isBalance, isCompact, liability);
  const legendItems = buildLegendItems(isBalance, BALANCE_COLOR, RATE_COLOR);

  const emptyMessage = isBalance
    ? "No balance history yet."
    : "No interest rate history yet.";

  // The hook only fetches the balance series; the rate series comes from the
  // already-loaded liability, so loading should only gate the balance tab.
  const isChartLoading = isBalance && isLoading;

  const chartHeight = isCompact ? MOBILE_CHART_HEIGHT : DESKTOP_CHART_HEIGHT;
  const lastIndex = chartData.values.length - 1;
  const yAxisFormatter = buildYAxisFormatter(isBalance);
  const yAxisMin = isBalance ? 0 : chartData.yMin;
  const yAxisMax = isBalance ? undefined : chartData.yMax;
  const yAxisWidth = isCompact ? 48 : 60;

  const series = useMemo(
    () => buildTrendSeries(isBalance, chartData.values, seriesColor, lastIndex),
    [isBalance, chartData.values, seriesColor, lastIndex],
  );

  const chartMargin = isCompact
    ? { top: 16, right: 12, bottom: 8, left: 8 }
    : { top: 20, right: 20, bottom: 8, left: 12 };

  const chartSx = {
    "& .MuiLineChart-area": { fill: `url(#${gradientId})` },
    "& .MuiLineElement-root": { strokeWidth: 2 },
    "& .MuiChartsAxis-tickLabel": {
      fontFamily: "'Crimson Pro', sans-serif",
      fill: "var(--color-text-secondary) !important",
    },
    "& .MuiChartsGrid-line": {
      stroke: "var(--color-border) !important",
      strokeOpacity: 0.6,
    },
    "& .MuiMarkElement-root": {
      fill: seriesColor,
      stroke: "var(--color-card-background)",
      strokeWidth: 2,
    },
  };

  const chart = (
    <ChartArea>
      <LineChart
        height={chartHeight}
        series={series}
        margin={chartMargin}
        grid={{ horizontal: true }}
        sx={chartSx}
        xAxis={[
          {
            data: chartData.labels,
            scaleType: "point",
            valueFormatter: (value: string) => formatMonthLabel(value),
            tickInterval: yearTickInterval,
            disableLine: true,
            disableTicks: true,
          },
        ]}
        yAxis={[
          {
            min: yAxisMin,
            max: yAxisMax,
            valueFormatter: (value: number) => yAxisFormatter(value),
            width: yAxisWidth,
            disableLine: true,
            disableTicks: true,
          },
        ]}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={seriesColor} stopOpacity={0.28} />
            <stop offset="100%" stopColor={seriesColor} stopOpacity={0.02} />
          </linearGradient>
        </defs>
      </LineChart>
    </ChartArea>
  );

  let chartContent: React.ReactNode = (
    <EmptyMessage>{emptyMessage}</EmptyMessage>
  );
  if (isChartLoading) {
    chartContent = <EmptyMessage>Loading…</EmptyMessage>;
  } else if (hasData) {
    chartContent = chart;
  }

  return (
    <TrendsCard aria-label="Liability trends chart">
      <HeaderRow>
        <TitleBlock>
          <Title>{title}</Title>
          <Subtitle>{subtitle}</Subtitle>
        </TitleBlock>
        <DesktopControl>
          <SegmentedControl
            options={TAB_OPTIONS}
            value={activeTab}
            onChange={setActiveTab}
            ariaLabel="Trend"
            testId="liability-trends-filter"
          />
        </DesktopControl>
      </HeaderRow>

      <LegendRow>
        {legendItems.map((item) => (
          <LegendItem key={item.label}>
            <LegendSwatch $color={item.color} $style={item.style} />
            {item.label}
          </LegendItem>
        ))}
        {!isBalance && (
          <HelperNote>Axis zoomed to the rate&apos;s range</HelperNote>
        )}
      </LegendRow>

      {chartContent}

      <MobileControl>
        <SegmentedControl
          options={TAB_OPTIONS}
          value={activeTab}
          onChange={setActiveTab}
          ariaLabel="Trend"
          testId="liability-trends-filter-mobile"
        />
      </MobileControl>
    </TrendsCard>
  );
};

export const LiabilityTrends = React.memo(LiabilityTrendsComponent);
